import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft, Download, Send, RefreshCw, XCircle, Search, Mail, Save, Edit2,
  CheckCircle, Clock, AlertTriangle, Info,
} from 'lucide-react'
import {
  getKostenuebernahmeDetail,
  updateEmpfaenger,
  sendErklaerung,
  reissueErklaerung,
  cancelErklaerung,
  rematchErklaerung,
  resendBuchhaltung,
  pdfDownloadUrl,
} from '@/api/kostenuebernahme'
import type { KostenuebernahmeStatus, KostenuebernahmeAuditEntry } from '@/types/kostenuebernahme'
import Spinner from '@/components/Spinner'
import ErrorAlert from '@/components/ErrorAlert'

// ── Status helpers ────────────────────────────────────────────
const STATUS_LABELS: Record<KostenuebernahmeStatus, string> = {
  pending_review: 'Pruefen',
  sent: 'Versendet',
  in_progress: 'In Bearbeitung',
  signed: 'Signiert',
  cancelled: 'Storniert',
  pending_admin_approval: 'Admin-Prüfung',
  admin_rejected_takeover: 'Admin übernimmt',
  admin_rejected_no_coverage: 'Abgelehnt',
  unmatched_studi: 'Studi-Konflikt',
}
const STATUS_COLORS: Record<KostenuebernahmeStatus, string> = {
  pending_review: 'bg-amber-100 text-amber-700',
  sent: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-indigo-100 text-indigo-700',
  signed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500',
  pending_admin_approval: 'bg-purple-100 text-purple-700',
  admin_rejected_takeover: 'bg-orange-100 text-orange-700',
  admin_rejected_no_coverage: 'bg-rose-100 text-rose-700',
  unmatched_studi: 'bg-pink-100 text-pink-700',
}

// ── Audit event icons ─────────────────────────────────────────
function auditIcon(eventType: string) {
  if (eventType.includes('sent') || eventType.includes('send')) return <Send size={14} className="text-blue-500" />
  if (eventType.includes('sign')) return <CheckCircle size={14} className="text-emerald-500" />
  if (eventType.includes('cancel')) return <XCircle size={14} className="text-red-500" />
  if (eventType.includes('match') || eventType.includes('rematch')) return <Search size={14} className="text-purple-500" />
  if (eventType.includes('buchhal')) return <Mail size={14} className="text-indigo-500" />
  if (eventType.includes('update') || eventType.includes('empfaenger')) return <Edit2 size={14} className="text-yellow-600" />
  return <Info size={14} className="text-gray-400" />
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'gerade eben'
  if (mins < 60) return `vor ${mins} Min.`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `vor ${hrs} Std.`
  const days = Math.floor(hrs / 24)
  return `vor ${days} Tagen`
}

// ── Cancel Modal ──────────────────────────────────────────────
function CancelModal({
  onConfirm,
  onClose,
  loading,
}: {
  onConfirm: (reason: string) => void
  onClose: () => void
  loading: boolean
}) {
  const [reason, setReason] = useState('')
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-base font-bold text-gray-900 mb-3">Vorgang stornieren</h3>
        <p className="text-sm text-gray-600 mb-4">Bitte gib einen Grund fuer die Stornierung an.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none resize-none"
          placeholder="Stornierungsgrund..."
        />
        <div className="flex gap-2 mt-4">
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || loading}
            className="btn btn-primary disabled:opacity-50"
          >
            {loading ? <Spinner /> : <XCircle size={14} />}
            Stornieren
          </button>
          <button onClick={onClose} className="btn btn-outline">Abbrechen</button>
        </div>
      </div>
    </div>
  )
}

// ── Confirm Modal ─────────────────────────────────────────────
function ConfirmModal({
  title,
  message,
  onConfirm,
  onClose,
  loading,
}: {
  title: string
  message: string
  onConfirm: () => void
  onClose: () => void
  loading: boolean
}) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <h3 className="text-base font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-5">{message}</p>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-all duration-150 cursor-pointer">
            Abbrechen
          </button>
          <button onClick={onConfirm} disabled={loading} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium bg-fom-500 text-white border border-transparent hover:bg-fom-600 transition-all duration-150 cursor-pointer disabled:opacity-50">
            {loading ? <Spinner /> : null}
            Bestaetigen
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Audit Timeline ────────────────────────────────────────────
function AuditTimeline({ entries }: { entries: KostenuebernahmeAuditEntry[] }) {
  const sorted = [...entries].sort(
    (a, b) => new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime(),
  )
  return (
    <div className="space-y-3">
      {sorted.map((entry) => (
        <div key={entry.Id} className="flex gap-3">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5">
            {auditIcon(entry.EventType)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-gray-800">{entry.EventType}</span>
              {entry.Actor && (
                <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                  {entry.Actor}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-gray-400" title={new Date(entry.CreatedAt).toLocaleString('de-DE')}>
                {relativeTime(entry.CreatedAt)} · {new Date(entry.CreatedAt).toLocaleString('de-DE')}
              </span>
            </div>
            {entry.DetailsJson && (() => {
              try {
                const parsed = JSON.parse(entry.DetailsJson)
                return (
                  <pre className="mt-1 text-[10px] text-gray-500 bg-gray-50 rounded p-1.5 overflow-x-auto max-w-full">
                    {JSON.stringify(parsed, null, 2)}
                  </pre>
                )
              } catch {
                return (
                  <p className="text-[10px] text-gray-400 mt-0.5">{entry.DetailsJson}</p>
                )
              }
            })()}
          </div>
        </div>
      ))}
      {sorted.length === 0 && (
        <p className="text-xs text-gray-400 text-center py-4">Kein Audit-Eintrag vorhanden.</p>
      )}
    </div>
  )
}

// ── Info Row helper ───────────────────────────────────────────
function InfoRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div>
      <dt className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-gray-900 mt-0.5">{value ?? '–'}</dd>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────
export default function KostenuebernahmenDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const numId = Number(id)

  const queryKey = ['kostenuebernahme', numId]

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: () => getKostenuebernahmeDetail(numId),
    enabled: !isNaN(numId),
  })

  // Edit state
  const [editingEmpfaenger, setEditingEmpfaenger] = useState(false)
  const [empfaengerFirma, setEmpfaengerFirma] = useState('')
  const [empfaengerApName, setEmpfaengerApName] = useState('')
  const [empfaengerApEmail, setEmpfaengerApEmail] = useState('')

  // Modal state
  const [modal, setModal] = useState<
    | { type: 'send' }
    | { type: 'reissue' }
    | { type: 'rematch' }
    | { type: 'buchhaltung' }
    | { type: 'cancel' }
    | null
  >(null)

  // Mutations
  const invalidate = () => qc.invalidateQueries({ queryKey })

  const updateEmpfaengerMut = useMutation({
    mutationFn: () =>
      updateEmpfaenger(numId, {
        Firma: empfaengerFirma,
        ApName: empfaengerApName,
        ApEmail: empfaengerApEmail,
      }),
    onSuccess: () => { setEditingEmpfaenger(false); invalidate() },
  })

  const sendMut = useMutation({ mutationFn: () => sendErklaerung(numId), onSuccess: () => { setModal(null); invalidate() } })
  const reissueMut = useMutation({ mutationFn: () => reissueErklaerung(numId), onSuccess: () => { setModal(null); invalidate() } })
  const rematchMut = useMutation({ mutationFn: () => rematchErklaerung(numId), onSuccess: () => { setModal(null); invalidate() } })
  const buchhaltungMut = useMutation({ mutationFn: () => resendBuchhaltung(numId), onSuccess: () => { setModal(null); invalidate() } })
  const cancelMut = useMutation({
    mutationFn: (reason: string) => cancelErklaerung(numId, reason),
    onSuccess: () => { setModal(null); invalidate() },
  })

  if (isLoading) return <Spinner className="mt-20" />
  if (error) return <ErrorAlert message={(error as Error).message} onRetry={refetch} />
  if (!data) return null

  const { item, audit } = data
  const status = item.Status

  const studiName = [item.StudiAnrede, item.StudiVorname, item.StudiNachname].filter(Boolean).join(' ') || '–'

  function startEditEmpfaenger() {
    setEmpfaengerFirma(item.ZuzahlerFirma ?? '')
    setEmpfaengerApName(item.ZuzahlerApName ?? '')
    setEmpfaengerApEmail(item.ZuzahlerApEmail ?? '')
    setEditingEmpfaenger(true)
  }

  const anyMutLoading =
    sendMut.isPending || reissueMut.isPending || rematchMut.isPending ||
    buchhaltungMut.isPending || cancelMut.isPending

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/superadmin/kostenuebernahmen')} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{studiName}</h1>
          <p className="text-xs text-gray-400">Vorgang #{item.Id} · Anmeldung #{item.AnmeldungJsonId}</p>
        </div>
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[status]}`}>
          {STATUS_LABELS[status]}
        </span>
      </div>

      {/* Mutation errors */}
      {sendMut.error && <ErrorAlert message={(sendMut.error as Error).message} />}
      {reissueMut.error && <ErrorAlert message={(reissueMut.error as Error).message} />}
      {rematchMut.error && <ErrorAlert message={(rematchMut.error as Error).message} />}
      {buchhaltungMut.error && <ErrorAlert message={(buchhaltungMut.error as Error).message} />}
      {cancelMut.error && <ErrorAlert message={(cancelMut.error as Error).message} />}
      {updateEmpfaengerMut.error && <ErrorAlert message={(updateEmpfaengerMut.error as Error).message} />}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Linke Spalte */}
        <div className="flex-[2] space-y-5">

          {/* Studi-Block */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">Studierende/r</h2>
            <dl className="grid grid-cols-2 gap-4">
              <InfoRow label="Name" value={studiName} />
              <InfoRow label="Geburtsdatum" value={item.StudiBirthdate ? new Date(item.StudiBirthdate).toLocaleDateString('de-DE') : null} />
              <InfoRow label="Matrikel" value={item.StudiMatrikel} />
              <InfoRow label="Studienort" value={item.StudiStudienort} />
              <InfoRow label="Studienbeginn" value={item.StudiStudienbeginn} />
            </dl>
          </section>

          {/* Zuzahler-Block */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Zuzahler</h2>
              {status === 'pending_review' && !editingEmpfaenger && (
                <button onClick={startEditEmpfaenger} className="btn btn-outline text-xs py-1 px-2 flex items-center gap-1">
                  <Edit2 size={12} />
                  Bearbeiten
                </button>
              )}
            </div>

            {editingEmpfaenger ? (
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Firma</label>
                  <input
                    type="text"
                    value={empfaengerFirma}
                    onChange={(e) => setEmpfaengerFirma(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Ansprechpartner Name</label>
                  <input
                    type="text"
                    value={empfaengerApName}
                    onChange={(e) => setEmpfaengerApName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Ansprechpartner E-Mail</label>
                  <input
                    type="email"
                    value={empfaengerApEmail}
                    onChange={(e) => setEmpfaengerApEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fom-500 outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => updateEmpfaengerMut.mutate()}
                    disabled={updateEmpfaengerMut.isPending}
                    className="btn btn-primary text-xs py-1 px-3 flex items-center gap-1 disabled:opacity-50"
                  >
                    <Save size={12} />
                    {updateEmpfaengerMut.isPending ? 'Speichert...' : 'Speichern'}
                  </button>
                  <button onClick={() => setEditingEmpfaenger(false)} className="btn btn-outline text-xs py-1 px-3">
                    Abbrechen
                  </button>
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-4">
                <InfoRow label="Firma" value={item.ZuzahlerFirma} />
                <InfoRow label="Ansprechpartner" value={item.ZuzahlerApName} />
                <InfoRow label="E-Mail AP" value={item.ZuzahlerApEmail} />
              </dl>
            )}
          </section>

          {/* CRM-Match-Info */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">CRM-Match</h2>
            <dl className="grid grid-cols-2 gap-4">
              <InfoRow label="Methode" value={item.MatchMethod} />
              <InfoRow label="Konfidenz" value={item.MatchConfidence} />
              <div>
                <dt className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">Account-ID</dt>
                <dd className="text-sm mt-0.5">
                  {item.CrmAccountId ? (
                    <span className="font-mono text-gray-800">{item.CrmAccountId}</span>
                  ) : '–'}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium text-gray-400 uppercase tracking-wide">Contact-ID</dt>
                <dd className="text-sm mt-0.5">
                  {item.CrmContactId ? (
                    <span className="font-mono text-gray-800">{item.CrmContactId}</span>
                  ) : '–'}
                </dd>
              </div>
            </dl>
          </section>

          {/* Zahlungsinfo */}
          {(item.AnteilProzent !== null && item.AnteilProzent !== undefined) && (
            <section className="bg-white border border-gray-200 rounded-lg p-5">
              <h2 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">Zahlungsdetails</h2>
              <dl className="grid grid-cols-2 gap-4">
                <InfoRow label="Anteil" value={item.AnteilProzent != null ? `${item.AnteilProzent} %` : null} />
                <InfoRow label="Zahlungsart" value={item.Zahlungsart} />
                <InfoRow label="Zahlungsweise" value={item.Zahlungsweise} />
                <InfoRow label="Rechnungs-Firma" value={item.RechnungFirma} />
                <InfoRow label="Rechnungs-AP" value={item.RechnungApName} />
                <InfoRow label="Rechnungs-E-Mail" value={item.RechnungEmailAp} />
              </dl>
            </section>
          )}

          {/* Timestamps */}
          <section className="bg-white border border-gray-200 rounded-lg p-5">
            <h2 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider">Zeitstempel</h2>
            <dl className="grid grid-cols-2 gap-4">
              <InfoRow label="Erstellt" value={new Date(item.CreatedAt).toLocaleString('de-DE')} />
              <InfoRow label="Aktualisiert" value={new Date(item.UpdatedAt).toLocaleString('de-DE')} />
              {item.SignedAt && <InfoRow label="Signiert" value={new Date(item.SignedAt).toLocaleString('de-DE')} />}
              {item.BuchhaltungMailSentAt && <InfoRow label="Buchhaltung versendet" value={new Date(item.BuchhaltungMailSentAt).toLocaleString('de-DE')} />}
              {item.CancelledAt && <InfoRow label="Storniert" value={new Date(item.CancelledAt).toLocaleString('de-DE')} />}
              {item.CancelledBy && <InfoRow label="Storniert von" value={item.CancelledBy} />}
              {item.CancelledReason && <InfoRow label="Stornierungsgrund" value={item.CancelledReason} />}
            </dl>
          </section>
        </div>

        {/* Rechte Spalte – Audit-Log */}
        <div className="flex-[1] min-w-0">
          <section className="bg-white border border-gray-200 rounded-lg p-5 sticky top-6">
            <h2 className="text-sm font-bold text-gray-700 mb-4 uppercase tracking-wider flex items-center gap-2">
              <Clock size={14} className="text-gray-400" />
              Audit-Log
            </h2>
            <AuditTimeline entries={audit} />
          </section>
        </div>
      </div>

      {/* Action-Bar */}
      <div className="mt-6 bg-white border border-gray-200 rounded-lg px-5 py-4 flex flex-wrap gap-3 items-center">
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-2">Aktionen</span>

        {status === 'pending_review' && (
          <>
            <button
              onClick={startEditEmpfaenger}
              className="btn btn-outline flex items-center gap-1.5 text-sm"
            >
              <Edit2 size={14} />
              Empfaenger korrigieren
            </button>
            <button
              onClick={() => setModal({ type: 'send' })}
              disabled={anyMutLoading}
              className="btn btn-primary flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <Send size={14} />
              Anforderung senden
            </button>
            <button
              onClick={() => setModal({ type: 'rematch' })}
              disabled={anyMutLoading}
              className="btn btn-outline flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <Search size={14} />
              Re-Match
            </button>
            <button
              onClick={() => setModal({ type: 'cancel' })}
              disabled={anyMutLoading}
              className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50 flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <XCircle size={14} />
              Verwerfen
            </button>
          </>
        )}

        {(status === 'sent' || status === 'in_progress') && (
          <>
            <button
              onClick={() => setModal({ type: 'reissue' })}
              disabled={anyMutLoading}
              className="btn btn-primary flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <RefreshCw size={14} />
              Link neu senden
            </button>
            <button
              onClick={() => setModal({ type: 'cancel' })}
              disabled={anyMutLoading}
              className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50 flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <XCircle size={14} />
              Stornieren
            </button>
          </>
        )}

        {status === 'signed' && (
          <>
            <a
              href={pdfDownloadUrl(item.Id)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline flex items-center gap-1.5 text-sm"
            >
              <Download size={14} />
              PDF herunterladen
            </a>
            <button
              onClick={() => setModal({ type: 'buchhaltung' })}
              disabled={anyMutLoading}
              className="btn btn-outline flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <Mail size={14} />
              Erneut an Buchhaltung
            </button>
            <button
              onClick={() => setModal({ type: 'cancel' })}
              disabled={anyMutLoading}
              className="btn btn-outline text-red-600 border-red-300 hover:bg-red-50 flex items-center gap-1.5 text-sm disabled:opacity-50"
            >
              <XCircle size={14} />
              Stornieren
            </button>
          </>
        )}

        {status === 'cancelled' && item.PdfPath && (
          <a
            href={pdfDownloadUrl(item.Id)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline flex items-center gap-1.5 text-sm"
          >
            <Download size={14} />
            PDF herunterladen
          </a>
        )}

        {status === 'cancelled' && !item.PdfPath && (
          <span className="text-sm text-gray-400 flex items-center gap-1.5">
            <AlertTriangle size={14} />
            Vorgang storniert - keine weiteren Aktionen
          </span>
        )}
      </div>

      {/* Modals */}
      {modal?.type === 'cancel' && (
        <CancelModal
          onConfirm={(reason) => cancelMut.mutate(reason)}
          onClose={() => setModal(null)}
          loading={cancelMut.isPending}
        />
      )}

      {modal?.type === 'send' && (
        <ConfirmModal
          title="Anforderung senden"
          message="Soll die Kostenuebernahme-Anforderung jetzt an den Zuzahler versendet werden?"
          onConfirm={() => sendMut.mutate()}
          onClose={() => setModal(null)}
          loading={sendMut.isPending}
        />
      )}

      {modal?.type === 'reissue' && (
        <ConfirmModal
          title="Link neu senden"
          message="Soll ein neuer Unterschriften-Link an den Zuzahler gesendet werden?"
          onConfirm={() => reissueMut.mutate()}
          onClose={() => setModal(null)}
          loading={reissueMut.isPending}
        />
      )}

      {modal?.type === 'rematch' && (
        <ConfirmModal
          title="CRM-Re-Match starten"
          message="Soll der Zuzahler erneut im CRM gesucht und gematched werden?"
          onConfirm={() => rematchMut.mutate()}
          onClose={() => setModal(null)}
          loading={rematchMut.isPending}
        />
      )}

      {modal?.type === 'buchhaltung' && (
        <ConfirmModal
          title="Erneut an Buchhaltung senden"
          message="Soll das signierte Dokument erneut an die Buchhaltung gemailt werden?"
          onConfirm={() => buchhaltungMut.mutate()}
          onClose={() => setModal(null)}
          loading={buchhaltungMut.isPending}
        />
      )}
    </div>
  )
}
